import dayjs from 'dayjs';
import numeral from 'numeral';
import isNil from 'lodash/isNil';
import isNaN from 'lodash/isNaN';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import isArray from 'lodash/isArray';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';
import toLower from 'lodash/toLower';
import map from 'lodash/map';
import round from 'lodash/round';
import filter from 'lodash/filter';
import identity from 'lodash/identity';
import moment from 'moment';
import escapeRegExp from 'lodash/escapeRegExp';
import isNumber from 'lodash/isNumber';
import join from 'lodash/join';
import reduce from 'lodash/reduce';
import isObjectLike from 'lodash/isObjectLike';

export const floatParse = (input: string, removeChars?: boolean): number => {
  if (removeChars) {
    const sign = input.startsWith('-') ? -1 : 1;
    return sign * parseFloat(input.replace(/[^\d.]/g, ''));
  }
  return parseFloat(input.replace(/[$,]/g, ''));
};

/**
 * With currency amount,
 * 1. negative numbers can be within parentheses, for example (55.00) means -55
 * 2. negative numbers can have '-'
 *    - before the number and currency sign, for example -$55.00 means -55
 *    - before the number and after the currency sign, for example $-55.00 means -55
 *    - after the number, for example 55.00- means -55
 * @param input
 * @param removeChars
 */
export const currencyParse = (input: string, removeChars?: boolean): number => {
  // This regex needs to take "," into consideration
  if (input.match(/\(\$?[\d,]+(\.\d+)?\)/)) {
    return -floatParse(input, true);
  }
  if (removeChars) {
    const sign = input.startsWith('-') || input.startsWith('$-') || input.endsWith('-') ? -1 : 1;
    return sign * parseFloat(input.replace(/[^\d.]/g, ''));
  }
  return parseFloat(input.replace(/[$,]/g, ''));
};

export const stripNonAlphaNum = (input: string) => {
  return input.replace(/[^A-Za-z\d]/gi, '');
};

export const stripNonNumeric = (input: string) => {
  return input.replace(/\D/g, '');
};

/**
 * Remove commas from input
 */
export const stripComma = (input: string) => {
  return input.replace(/,/g, '');
};

export enum FindMatchEnum {
  Includes = 'Includes',
  StartsWith = 'StartsWith',
  EndsWith = 'EndsWith',
  Equals = 'Equals',
}

interface IFindMatchArgs {
  type?: FindMatchEnum;
  caseSensitive?: boolean;
}

/**
 * Returns first value in inputMatches that is a match for any of 'input' strings. Default is case-insensitive includes
 * @param input input string or list of strings to search
 * @param inputMatches input string or list of strings to look for in input
 * @param args matchType: includes, starts with, ends with, equals
 *             caseSensitive: true or false/undefined
 */
export const findTextMatch = (
  input: string | string[],
  inputMatches: string | string[],
  args: IFindMatchArgs = {}
): string | null => {
  // default match type to include-case-insensitive
  const { type: matchType = FindMatchEnum.Includes, caseSensitive = false } = args;
  let regexText: string;
  const inputArray = typeof input !== 'string' ? input : [input];
  const inputMatchesArray = typeof inputMatches !== 'string' ? inputMatches : [inputMatches];
  const transformFn: (s: string) => string = !caseSensitive ? toLower : identity;

  switch (matchType) {
    case FindMatchEnum.Includes: {
      const inputBlock = typeof input !== 'string' ? join(input, '\n') : input;
      const searchText = transformFn(inputBlock);
      if (typeof inputMatches === 'string') {
        return searchText.includes(transformFn(inputMatches)) ? inputMatches : null;
      }
      for (const match of inputMatches) {
        if (searchText.includes(transformFn(match))) {
          return match;
        }
      }
      return null;
    }
    case FindMatchEnum.Equals: {
      const transformedInputArray = map(inputArray, (val) => transformFn(val));
      for (const match of inputMatchesArray) {
        if (transformedInputArray.includes(transformFn(match))) {
          return match;
        }
      }
      return null;
    }
    case FindMatchEnum.StartsWith: {
      const escapedMatchesArray = inputMatchesArray.map((match) => escapeRegExp(match));
      regexText = `^(${join(escapedMatchesArray, '|')})`;
      break;
    }
    case FindMatchEnum.EndsWith: {
      const escapedMatchesArray = inputMatchesArray.map((match) => escapeRegExp(match));
      regexText = `(${join(escapedMatchesArray, '|')})$`;
      break;
    }
  }

  // handle regex cases. use 'i' for case-insensitive
  if (!regexText) {
    throw new Error('Expected regex case but has regexText in missing');
  }
  const regex = new RegExp(regexText, !caseSensitive ? 'i' : undefined);
  for (const oneInput of inputArray) {
    const match = regex.exec(oneInput);
    if (match) {
      return match[1]; // return matching sub-expression, the actual matching string from inputMatches
    }
  }
  return null;
};

/**
 * Return first match in inputMatches that is included case-insensitive in 'input'
 * @param input input string or list of strings to search
 * @param inputMatches input string or list of strings to look for in input
 */
export const findInsensitiveMatch = (input: string | string[], inputMatches: string | string[]): string | null => {
  // default match type to include-case-insensitive
  return findTextMatch(input, inputMatches, { type: FindMatchEnum.Includes, caseSensitive: false });
};

/**
 * Returns true IF input has a match among inputMatches
 * @param input input string or list of strings to search
 * @param inputMatches input string or list of strings to look for in input
 * @param args matchType: includes, starts with, ends with, equals
 *             caseSensitive: true or false/undefined
 */
export const hasTextMatch = (input: string | string[], inputMatches: string | string[], args: IFindMatchArgs = {}) => {
  // default match type to include-case-insensitive
  const { type = FindMatchEnum.Includes, caseSensitive = false } = args;
  const match = findTextMatch(input, inputMatches, { type, caseSensitive });
  return match !== null;
};

/**
 * Returns true IF input has a match among inputMatches case-insensitive
 * @param input input string or list of strings to search
 * @param inputMatches input string or list of strings to look for in input
 */
export const includesCaseInsensitive = (input: string | string[], inputMatches: string | string[]) => {
  return hasTextMatch(input, inputMatches, { type: FindMatchEnum.Includes, caseSensitive: false });
};

/**
 * Returns true IF input has a match for every inputMatches case-insensitive
 * @param input input string or list of strings to search
 * @param inputMatches input string or list of strings to look for in input
 */
export const includesCaseInsensitiveEvery = (input: string | string[], inputMatches: string[]) => {
  return reduce(
    inputMatches,
    (result, inputMatch) =>
      result && hasTextMatch(input, inputMatch, { type: FindMatchEnum.Includes, caseSensitive: false }),
    true
  );
};

/**
 * Returns true IF input and inputMatch are equals ignoring upper/lower case differences.
 * @param input
 * @param inputMatch
 */
export const equalsCaseInsensitive = (
  input: string | null | undefined,
  inputMatch: string | null | undefined
): boolean => {
  if (!input || !inputMatch) {
    return input === inputMatch;
  }
  return input.toLowerCase() === inputMatch.toLowerCase();
};

/**
 * Returns true IF one of strings in inputs is an insensitive exact match of inputMatch
 * @param inputs input strings
 * @param inputMatch string to match the input strings against
 */
export const hasInsensitiveExactMatch = (inputs: (string | undefined)[], inputMatch: string | undefined | null) => {
  if (isNil(inputMatch)) return false;
  const match = inputs.find((input) => equalsCaseInsensitive(input, inputMatch));
  return match !== undefined;
};

/**
 * Type-guard to test if a value exists. Useful for narrowing type during filter
 */
export function exists<TValue>(value?: TValue): value is TValue {
  return !!value;
}

/**
 * Rounds given number to cents
 * @param value number to round
 */
export const roundToCents = (value: number): number => parseFloat(value.toFixed(2));

/**
 * Rounds given number to cents as string preserving any trailing 0s
 * @param value
 * @returns
 */
export const roundToCentsAsString = (value: number): string => value.toFixed(2);

export const cleanObject = <T = string>(originalObject: { [key: string]: T | undefined }) => {
  return pickBy(originalObject, (v) => v !== undefined) as { [key: string]: T };
};

/**
 * Splits coverages like 100/200 into an array of 2 numbers.
 */
export const splitCoverageValues = (value: string): number[] =>
  value
    .split('/')
    .map((x) => floatParse(x, true))
    .slice(0, 2);

/**
 * Phone number formatting
 */
export const formatPhone = (numString: string, addParentheses = true, addHyphen = false): string => {
  const num = numString.replace(/\D/g, '');
  const p1 = num.slice(0, 3);
  const p2 = num.slice(3, 6);
  const p3 = num.slice(6, 10);

  if (p1.length < 1) {
    return '';
  }
  if (p1.length < 3 || p2.length < 1) {
    return `(${p1}`;
  }
  if (p2.length < 3 || p3.length < 1) {
    return `(${p1}) ${p2}`;
  }

  return addParentheses
    ? addHyphen
      ? `(${p1}) ${p2}-${p3}`
      : `(${p1}) ${p2} ${p3}`
    : addHyphen
      ? `${p1}-${p2}-${p3}`
      : `${p1} ${p2} ${p3}`;
};

/**
 * Convert formatted phone number into string of numeric digits.
 * Removes everything except numbers from phone
 * @param inputNum
 * @param takeHead
 * @param stripCountryCode
 */
export const unformatPhone = (inputNum: string, takeHead = true, stripCountryCode = true): string => {
  let num = inputNum.trim();
  if (stripCountryCode) {
    // Remove starting "+1" if existing
    num = num.replace(/^\+1/, '');
  }
  return num.replace(/\D/g, '').slice(takeHead ? 0 : -10, takeHead ? 10 : undefined);
};

/**
 * SSN formatting
 */
export const formatSSN = (num: string): string => {
  const p1 = num.slice(0, 3);
  const p2 = num.slice(3, 5);
  const p3 = num.slice(5, 9);

  if (p1.length < 1) {
    return '';
  }
  if (p1.length < 3 || p2.length < 1) {
    return `${p1}`;
  }
  if (p2.length < 2 || p3.length < 1) {
    return `${p1}-${p2}`;
  }

  return `${p1}-${p2}-${p3}`;
};

export const unformatSSN = (num: string): string => {
  return num.replace(/\D/g, '').substr(0, 9);
};

/**
 * Format how a date is displayed in the dashboard
 * @param num
 * @param format
 */
export const formatDate = (num: string | Date, format = 'MMMM D, YYYY') =>
  !!num && dayjs(num).isValid() ? dayjs(num).format(format) : '';

/**
 * Date string formatting: MM-DD-YYYY
 */
export const formatDateString = (num: string, delimiter = '-'): string => {
  const p1 = num.slice(0, 2);
  const p2 = num.slice(2, 4);
  const p3 = num.slice(4, 8);

  if (p1.length < 1) {
    return '';
  }
  if (p1.length < 2 || p2.length < 1) {
    return p1;
  }
  if (p2.length < 2 || p3.length < 1) {
    return `${p1}${delimiter}${p2}`;
  }

  return `${p1}${delimiter}${p2}${delimiter}${p3}`;
};

/**
 * Format number of months to "X years Y months" string
 *
 * @example 12 => 1 year
 * @example 13 => 1 year 1 month
 * @example 49 => 4 years 1 month
 * @example 6 => 6 months
 */
export const formatMonthsWords = (months: number): string => {
  const year = Math.floor(months / 12);
  const month = months % 12;
  return join(
    filter([
      year ? `${year} year${year === 1 ? '' : 's'}` : '',
      month ? `${month} month${month === 1 ? '' : 's'}` : '',
    ]),
    ' '
  );
};

/**
 * Format name by capitalizing the first character
 */
export const formatName = (name: string | null): string => {
  if (!name) return '';
  const nameArr = name.toLocaleLowerCase().split(' ');
  return join(
    map(nameArr, (p) => `${(p[0] || '').toLocaleUpperCase()}${p.substr(1)}`),
    ' '
  );
};

/**
 * Format credit card number to format: XXXX XXXX XXXX XXXX
 * @param num
 */
export const formatCardNumber = (num: string) => {
  const p1 = num.slice(0, 4);
  const p2 = num.slice(4, 8);
  const p3 = num.slice(8, 12);
  const p4 = num.slice(12, 16);

  if (p1.length < 1) {
    return '';
  }
  if (p1.length < 4 || p2.length < 1) {
    return `${p1}`;
  }
  if (p2.length < 4 || p3.length < 1) {
    return `${p1} ${p2}`;
  }
  if (p3.length < 4 || p4.length < 1) {
    return `${p1} ${p2} ${p3}`;
  }

  return `${p1} ${p2} ${p3} ${p4}`;
};

/**
 * Unformat credit card number
 *
 * @param num
 */
export const unformatCardNumber = (num: string) => num.replace(/\D/g, '').substr(0, 16);

/**
 * Format card expiration date to format: MM/YY
 * in forms we store it in MMYY format, displaying as MM/YY
 *
 * @param num
 */
export const formatCardExpiration = (num: string) => {
  const p1 = num.slice(0, 2);
  const p2 = num.slice(2, 4);

  if (p1.length < 1) {
    return '';
  }
  if (p1.length < 2 || p2.length < 1) {
    return `${p1}`;
  }

  return `${p1}/${p2}`;
};

/**
 * Un-format card expiration date: from MM/YY format to just MMYY string format
 * in forms we store it in MMYY format, displaying as MM/YY
 *
 * @param num
 */
export const unformatCardExpiration = (num: string) => num.replace(/\D/g, '').substr(0, 4);

/**
 * Format card CVC to format: XXX / XXXX
 *
 * @param num
 * @param cvcLengthLimit
 */
export const formatCardCVC = (num: string, cvcLengthLimit = 3) => num.slice(0, cvcLengthLimit);

/**
 * Un-format card CVC
 *
 * @param num
 * @param cvcLengthLimit
 */
export const unformatCardCVC = (num: string, cvcLengthLimit = 3) => num.replace(/\D/g, '').substr(0, cvcLengthLimit);

/**
 * Format Zip
 *
 * @param num
 */
export const formatZip = (num: string) => num.slice(0, 5);

/**
 * Un-format Zip
 */
export const unformatZip = (num: string) => num.replace(/\D/g, '').substr(0, 5);

/**
 * Format money (With comma and decimal)
 *
 * @param money - optional, number or false, to support && operator.
 * @param defaultValue
 * @param formatter
 */
export const formatMoney = (money?: number | false | null, defaultValue = '--', formatter?: string): string =>
  isNil(money) || money === false || isNaN(money)
    ? defaultValue
    : numeral(round(money, 2)).format(formatter || '$0,0[.]00');

/**
 * Converts number into short dollar value. 10000 => $10k
 * @param input
 */
export const shortMoneyConverter = (input: number): string => {
  if (input >= 1000000) {
    return `$${round(input / 1000000, 2)}m`;
  }
  if (input >= 1000) {
    return `$${round(input / 1000, 2)}k`;
  }
  return `$${round(input, 2)}`;
};

/**
 * Converts number into long dollar value. 10000 => $10,000, 1.23 => $1.23
 * @param input numerical input to convert to money format
 * @param format optional format parameter for converting the input
 */
export const longMoneyConverter = (input: number | string, format?: string): string => {
  return numeral(input).format(format || '$0,0[.]00');
};

/**
 * Converts number into dollar value
 */
export const moneyConverter = (input: number | string): string => {
  const ciValue = isNumber(input) ? input : parseInt(input, 10);
  if (ciValue % 1000000 === 0 || ciValue % 1000 === 0) {
    return shortMoneyConverter(ciValue);
  }

  return longMoneyConverter(ciValue);
};

/**
 * Formats float to two decimal places
 */
export const formatFloat = (num: string | number): string => {
  const val = `${num}`.match(/^\d+(\.\d?\d?)?/);
  return (val && val[0]) || '';
};

/**
 * Fill string with spaces up to desired length
 *
 * zFill('boo', 5)
 * >>> "boo  "
 *
 * zFill('booboo', 5)
 * >>> "booboo"
 *
 * zFill('boo', 5, +)
 * >>> "boo++"
 *
 * zFill('booboo', 5, undefined, true);
 * >>> "boobo"
 *
 * @param s - string
 * @param length - target string length
 * @param symbol - symbol to fill with
 * @param truncate - do trim to target length if string is longer
 */
export const zFill = (s: string, length: number, symbol = ' ', truncate = false): string => {
  if (s.length > length) {
    if (truncate) {
      return s.slice(0, length);
    }
    return s;
  }
  return s + symbol.repeat(length - s.length);
};

/**
 * Return currency value in CENTS supposing US input format (. - cents delimiter)
 */
export const parseUSDollarsValue = (value: string | number): number => {
  // undefined, null and all other false-casting values will be taken as 0
  const cleanValue = `${value || 0}`.replace(/[^\d.-]/g, '');

  // Check dots
  const dotsCount = (cleanValue.match(/\./g) || []).length;
  if (dotsCount > 1 || (dotsCount === 1 && cleanValue.length === 1)) {
    // more than one dot or '.'
    throw new Error(`Invalid numeric value: ${value} (cleaned: ${cleanValue})`);
  }

  // Check dashes
  const dashesOrMinusesCount = (cleanValue.match(/-/g) || []).length;
  if (dashesOrMinusesCount > 1 || cleanValue.search(/-/g) > 0) {
    // Many dashes or dash not at the beginning
    throw new Error(`Invalid numeric value: ${value} (cleaned: ${cleanValue})`);
  }

  const [integerPart, fractionalPart] = cleanValue.split('.');
  //                      '' is OK              Add zeroes or truncate to 2
  const centsString = (integerPart || '') + zFill(fractionalPart || '', 2, '0', true);
  return parseInt(centsString, 10);
};

/**
 * Truncates text to the max length, adding ellipsis at the end.
 *
 * @param text
 * @param maxLength
 */
export const truncateText = (text: string, maxLength: number) => {
  return text.length > maxLength ? `${text.substr(0, maxLength - 3)}...` : text;
};

/**
 * If value is not an array, plugs the scalar value into an array
 * and returns the array.
 * Example: forceToArray(1) => [1]
 *          forceToArray([1]) => [1]
 *          forceToArray(null | undefined) => []
 *
 * @param {*} val
 */
export const forceToArray = (val: unknown) => {
  if (isNull(val) || isUndefined(val)) return [];

  return isArray(val) ? val : [val];
};

/**
 * Repeatedly decodes the URI input until no change.
 * This is useful for cleaning a URI that has been `encodeURI` multiple times.
 *
 * @param uri - URI
 */
export const fullyDecodeURI = (uri: string): string => {
  const MAX_ITERATIONS = 5;
  let currURI = uri;
  let iter = 0;
  while (currURI !== decodeURI(currURI) && iter < MAX_ITERATIONS) {
    iter += 1;
    currURI = decodeURI(currURI);
  }

  if (currURI !== decodeURI(currURI))
    throw new Error(`Unable to fully decode URI: '${uri}'. Max iterations (${MAX_ITERATIONS}) reached.`);

  return currURI;
};

/**
 * Fully decodes the URI and then performs a single URI encoding on it.
 *
 * @param uri
 */
export const cleanEncodeURI = (uri: string): string => {
  return encodeURI(fullyDecodeURI(uri));
};

/**
 * Human-readable formatting of a car engine.
 *
 * @param engine
 */
export const formatCarEngine = (engine: string): string => {
  return engine.toUpperCase();
};

/**
 * Parse float, but divide by 100 if the string contains the percentage sign.
 *
 * @param {string} input
 * @returns {number}
 */
export const parsePercentage = (input: string): number => {
  if (/%/.test(input)) {
    return parseFloat(input.replace(/%/g, '')) / 100;
  }
  return parseFloat(input);
};

/**
 * Parse dollar value to cents
 * @param value
 */
export const parseValueToCents = (value: number): number => Math.round(value * 100);

/**
 * seo url param formatting
 */
export const formatUrlParam = (param: string): string => {
  return param.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
};

/**
 * Remove null and undefined field of data
 * @param data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const omitByIsNil = <T extends Record<string, any>>(data: T): T => omitBy(data, isNil) as T;

/**
 * Remove null, undefined, '', NaN from data
 * @param data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const omitByEmpty = <T extends Record<string, any>>(data: T): T =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  omitBy(data, (value: any) => isNil(value) || isNaN(value) || value === '') as T;

/**
 * Recusively remove null, undefined, '', NaN from data
 * @param data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const omitByEmptyRecusive = (data: Record<string, any>): Record<string, any> => {
  return reduce(
    data,
    (result, value, key) => {
      if (isObjectLike(value)) {
        result[key] = omitByEmptyRecusive(value);
      } else if (!(isNil(value) || isNaN(value) || value === '')) {
        result[key] = value;
      }
      return result;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {} as Record<string, any>
  );
};

/**
 * Mask params by '*' except last characters depending on digit param.
 * @param param
 * @param digit
 */
export const maskString = (param: string, digit = 4): string => {
  const regex = new RegExp('.(?=.{' + digit + '})', 'g');
  return param.replace(regex, '*');
};

/**
 * Get age from date of birth.
 *
 * @param dob
 */
export const formatDoBToAge = (dob: string): number => {
  return dayjs().diff(dob, 'year');
};

/**
 * Format numbers into number with comma.
 * 1000 -> 1,000
 */
export const numberWithCommas = (x): string => {
  return x.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
};

// Escape bad characters in json string
export const escapeJsonString = (text: string): string =>
  text
    .replace(/[\\]/g, '\\\\')
    .replace(/["]/g, '\\"')
    // eslint-disable-next-line no-useless-escape
    .replace(/[\/]/g, '\\/')
    .replace(/[\b]/g, '\\b')
    .replace(/[\f]/g, '\\f')
    .replace(/[\n]/g, '\\n')
    .replace(/[\r]/g, '\\r')
    .replace(/[\t]/g, '\\t');

// Convert given date to specified format
export const reformatDate = (date: string, formatFrom: string, formatTo: string): string => {
  if (!date || !moment(date, formatFrom, true).isValid()) {
    return '';
  }
  return moment(date, formatFrom, true).format(formatTo);
};

/**
 * Joins a list of strings using the given conjunction.
 * Examples:
 * - (['foo'], 'and') => 'foo'
 * - (['foo', 'bar'], 'and') => 'foo and bar'
 * - (['foo', 'bar', 'baz'], 'and') => 'foo, bar, and baz'
 * @param list strings to join
 * @param conjunction conjunction to use
 */
export const joinStringsWithConjunction = (list: string[], conjunction: 'and' | 'or'): string => {
  if (!list.length) {
    return '';
  }

  if (list.length === 1) {
    return list[0];
  }

  return [list.slice(0, -1).join(', '), list.slice(-1)].join(`${list.length > 2 ? ',' : ''} ${conjunction} `);
};

/**
 * Make the string not break in the middle when wrapping
 * @param input
 * @returns
 */
export const makeStringNonBreaking = (input: string): string => {
  if (!input) {
    return input;
  }
  return input.replace(/ /g, '\u00a0').replace(/-/g, '\u2011').replace(/\//g, '\u2215');
};
